import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SimpleHeader from '../components/SimpleHeader';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DollarSign, Gift, Lock, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FreeGuidePage = () => {
  const [email, setEmail] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/emails/send-free-guide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (!res.ok) throw new Error('Failed');
      setIsUnlocked(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const tips = [
    { number: 1, title: 'Audit Your Subscriptions in 10 Minutes', description: "Most people are paying for 2-4 subscriptions they forgot about. Pull up your bank or credit card statement, search for recurring charges, and list everything. Cancel anything you haven't used in the last 30 days. No guilt. The streaming service you might want 'someday' costs you $180/year. That's a weekend trip.", action: "Open your bank app right now. Scroll back 45 days. Write down every subscription charge. Cancel at least one today.", savings: '$20-$150/month' },
    { number: 2, title: 'Switch to a No-Fee Checking Account', description: 'Bank fees are invisible money leaks. Monthly maintenance fees, ATM fees, overdraft fees-these can cost $200+ per year for no reason. Online banks (Ally, Marcus, SoFi, Chime) offer zero-fee accounts, higher interest, and better apps than most traditional banks.', action: 'Open a free account at an online bank this week. Transfer your direct deposit. Close the fee-charging account after the first paycheck lands.', savings: '$100-$300/year' },
    { number: 3, title: 'Meal Plan Once, Save All Week', description: "Unplanned grocery shopping is the enemy of a lean budget. Without a list, you buy what looks good, forget what you have, and end up ordering takeout Thursday because nothing came together. Spend 15 minutes on Sunday: pick 4 dinners, write ingredients, shop once. The discipline isn't in resisting pizza-it's in not needing pizza because dinner is already handled.", action: 'Right now, plan 4 dinners for this week. Write the shopping list. Commit to one store trip only.', savings: '$150-$400/month' },
    { number: 4, title: 'Call Your Insurance and Ask for a Better Rate', description: "Insurance companies quietly raise rates every year. They bank on inertia. A 15-minute phone call to ask 'what discounts am I eligible for?' often yields $200-$600/year in savings-without switching providers. Bundle discounts, loyalty discounts, safety device discounts, paying annually instead of monthly. They won't offer these unless you ask.", action: "Call your auto or home insurer today. Say: 'I'm reviewing my budget. What discounts am I currently not getting?' Then call a competitor for a quote.", savings: '$200-$600/year' },
    { number: 5, title: 'Make One Meal from Scratch This Week', description: "Homemade food is 3-8x cheaper than restaurant or delivery food. A pot of soup: $4 and serves 6. The same soup at a restaurant: $14 per bowl. You don't need to become a chef. You need 3 reliable recipes you can make in 30 minutes. Start with one this week. The savings compound every time you repeat it.", action: 'Pick one delivery meal you order regularly. Find a recipe for it. Make it this week. Note the cost difference.', savings: '$200-$800/month' },
    { number: 6, title: 'Buy Generic for These 5 Categories', description: 'Brand loyalty is a marketing construct. For medications, cleaning products, dry goods (rice, oats, pasta), basic clothing (socks, undershirts), and paper products-store brands are identical or near-identical in quality at 20-40% less. The savings on medication alone (OTC generic vs. brand-name) can be $500+/year.', action: 'On your next shopping trip, swap to generic for any medications, cleaning supplies, and dry pantry staples.', savings: '$50-$200/month' },
    { number: 7, title: 'Negotiate Your Internet Bill', description: "Your internet provider does not want you to leave. The cancellation department has actual authority to offer you deals that the billing department doesn't. Call, say you're considering canceling, and ask what they can do. New customer promotions are often available to existing customers who ask. This works 70%+ of the time.", action: "Call your internet provider. Say: 'I'm looking at competitors and thinking about switching. What can you offer me to stay?' Have a competitor's price ready.", savings: '$20-$60/month' },
    { number: 8, title: 'Use the Library (For More Than Books)', description: 'Modern public libraries offer: ebooks and audiobooks (Libby/OverDrive), streaming services (Kanopy for films), digital magazines (Flipster/Pressreader), museum passes, seed libraries, tool lending, free classes, and 3D printers. A library card replaces $40-$80/month in subscriptions and rentals.', action: 'Get or renew your library card today. Download the Libby app. Cancel one paid subscription it replaces.', savings: '$40-$80/month' },
    { number: 9, title: 'Set a 48-Hour Rule for Non-Essential Purchases', description: "Impulse spending is the gap between income and savings for most people. Add anything non-essential to a 'wait list' instead of buying it immediately. Wait 48 hours. If you still want it and can afford it, buy it. If you forgot about it, you didn't need it. Research shows 30-50% of impulse purchases go unmade with a waiting period.", action: "Create a note on your phone called 'Wait List.' Next time you want to buy something non-essential, add it there instead. Review it in 48 hours.", savings: '$100-$400/month' },
    { number: 10, title: 'Repair Before Replacing', description: "We've been trained to treat everything as disposable. But a $15 repair kit extends the life of shoes by 2 years. A $8 appliance fuse fixes the microwave that would cost $200 to replace. Learning basic repairs-sewing, soldering, patching, plumbing washers-is one of the highest-ROI skills you can develop. Start with one item you've been meaning to replace.", action: "Identify one broken or worn-out item in your home you've been planning to replace. Search YouTube for 'how to repair [item].' Buy the $10 repair kit instead of the $80 replacement.", savings: '$200-$2,000/year' },
  ];

  const bonuses = [
    'Printable Audit Checklists',
    'Weekly Spending Planner',
    'Climate-Specific Plant Starter List',
    'DIY Project Cheat Sheets',
    '90-Day Implementation Plan'
  ];

  return (
    <div className="min-h-screen bg-[#F5F1E8]">
      <SimpleHeader />

      <main className="pt-24 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-[#6B7A5C] text-sm uppercase tracking-wider mb-4 font-semibold">
            Free Guide
          </p>

          <h1 className="text-5xl md:text-6xl font-bold text-[#2C1810] mb-6">
            10 Ways to Spend Less This Week
          </h1>

          <p className="text-[#5A5046] text-xl mb-12">
            Practical, immediately usable money-saving tips — no fluff, no sacrifice
          </p>

          {/* Email Gate */}
          {!isUnlocked && (
            <Card className="bg-white border-none shadow-lg p-8 mb-12" data-testid="email-gate-card">
              <div className="text-center">
                <div className="w-14 h-14 bg-[#6B7A5C] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-7 h-7 text-[#6B7A5C]" />
                </div>
                <h2 className="text-2xl font-bold text-[#2C1810] mb-2">
                  Enter your email to read the free guide
                </h2>
                <p className="text-[#5A5046] mb-6">
                  We'll also send you a copy so you can reference it anytime.
                </p>
                <form onSubmit={handleUnlock} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="guide-gate-form">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    className="flex-1 px-5 py-4 text-lg rounded-lg border-[#E5DFD0]"
                    disabled={isSubmitting}
                    data-testid="guide-gate-email-input"
                  />
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-[#6B7A5C] hover:bg-[#5A6850] text-white px-8 py-4 text-lg rounded-lg whitespace-nowrap"
                    data-testid="guide-gate-submit-btn"
                  >
                    {isSubmitting ? 'Unlocking...' : 'Unlock Guide'}
                  </Button>
                </form>
                {error && <p className="text-red-500 text-sm mt-3">{error}</p>}
                <p className="text-[#A89884] text-xs mt-4">No spam. Unsubscribe anytime.</p>
              </div>
            </Card>
          )}

          {/* Teaser (show first 2 tips blurred when locked) */}
          {!isUnlocked && (
            <div className="relative">
              {tips.slice(0, 2).map((tip) => (
                <Card key={tip.number} className="bg-white border-none shadow-md p-8 mb-8">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 rounded-full bg-[#6B7A5C] text-white flex items-center justify-center font-bold text-2xl">
                        {tip.number}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h2 className="text-3xl font-bold text-[#2C1810] mb-4">{tip.title}</h2>
                      <p className="text-[#5A5046] leading-relaxed mb-6">{tip.description}</p>
                      <div className="bg-[#EBE6D8] p-4 rounded-lg mb-4">
                        <p className="text-sm font-semibold text-[#2C1810] mb-2">Quick action:</p>
                        <p className="text-[#5A5046]">{tip.action}</p>
                      </div>
                      <div className="flex items-center gap-2 text-[#6B7A5C] font-semibold">
                        <DollarSign className="w-5 h-5" />
                        <span>Est. savings: {tip.savings}</span>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}

              {/* Blur overlay + lock prompt */}
              <div className="relative">
                <div className="blur-sm pointer-events-none opacity-50">
                  <Card className="bg-white border-none shadow-md p-8 mb-8">
                    <div className="flex items-start gap-6">
                      <div className="w-16 h-16 rounded-full bg-[#6B7A5C] text-white flex items-center justify-center font-bold text-2xl">3</div>
                      <div className="flex-1">
                        <h2 className="text-3xl font-bold text-[#2C1810] mb-4">Meal Plan Once, Save All Week</h2>
                        <p className="text-[#5A5046] leading-relaxed">Unplanned grocery shopping is the enemy of a lean budget. Without a list, you buy what looks good...</p>
                      </div>
                    </div>
                  </Card>
                </div>
                <div className="absolute inset-0 flex items-center justify-center" data-testid="guide-locked-overlay">
                  <div className="bg-white shadow-xl rounded-xl p-6 text-center max-w-sm border border-[#E5DFD0]">
                    <Lock className="w-8 h-8 text-[#C17855] mx-auto mb-3" />
                    <p className="text-[#2C1810] font-semibold mb-2">8 more tips locked</p>
                    <p className="text-[#5A5046] text-sm mb-4">Enter your email above to unlock all 10 tips for free.</p>
                    <button
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="text-[#6B7A5C] font-semibold text-sm hover:underline"
                    >
                      Scroll up to unlock
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Full content (unlocked) */}
          {isUnlocked && (
            <>
              <div className="bg-[#6B7A5C] bg-opacity-10 border border-[#6B7A5C] rounded-lg p-4 mb-12 text-center" data-testid="guide-unlocked-banner">
                <p className="text-[#2C1810] font-semibold">Guide unlocked! We also sent a copy to {email}</p>
              </div>

              <p className="text-[#5A5046] text-lg mb-16 leading-relaxed">
                These aren't vague suggestions. Each tip can save you money this week-some today. Pick three and start there.
              </p>

              <div className="space-y-12">
                {tips.map((tip) => (
                  <Card key={tip.number} className="bg-white border-none shadow-md p-8" data-testid={`tip-card-${tip.number}`}>
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 rounded-full bg-[#6B7A5C] text-white flex items-center justify-center font-bold text-2xl">
                          {tip.number}
                        </div>
                      </div>
                      <div className="flex-1">
                        <h2 className="text-3xl font-bold text-[#2C1810] mb-4">{tip.title}</h2>
                        <p className="text-[#5A5046] leading-relaxed mb-6">{tip.description}</p>
                        <div className="bg-[#EBE6D8] p-4 rounded-lg mb-4">
                          <p className="text-sm font-semibold text-[#2C1810] mb-2">Quick action:</p>
                          <p className="text-[#5A5046]">{tip.action}</p>
                        </div>
                        <div className="flex items-center gap-2 text-[#6B7A5C] font-semibold">
                          <DollarSign className="w-5 h-5" />
                          <span>Est. savings: {tip.savings}</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </>
          )}

          {/* CTA at bottom */}
          <div className="mt-20 text-center bg-gradient-to-br from-[#EBE6D8] to-[#D4C7B8] p-12 rounded-lg shadow-lg">
            <h2 className="text-3xl font-bold text-[#2C1810] mb-4">
              Ready to go deeper?
            </h2>
            <p className="text-[#5A5046] text-lg mb-8 max-w-2xl mx-auto">
              These 10 tips are the quick wins. The real transformation comes from building a system-one where spending less becomes the natural default, not a constant battle. The SpendLess Guide gives you the complete framework: 7 chapters covering permaculture-inspired finance, DIY systems, food sovereignty, energy independence, and a 90-day implementation plan.
            </p>

            <div className="bg-white border-2 border-[#6B7A5C] border-dashed rounded-lg p-6 mb-8 max-w-xl mx-auto">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Gift className="w-6 h-6 text-[#6B7A5C]" />
                <p className="font-bold text-[#2C1810]">+ Get $99 Worth of FREE Bonuses:</p>
              </div>
              <div className="space-y-2 text-sm text-left">
                {bonuses.map((bonus, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-[#6B7A5C] font-bold">&#10003;</span>
                    <span className="text-[#2C1810]">{bonus}</span>
                  </div>
                ))}
              </div>
            </div>

            <Link to="/products/guide">
              <Button className="bg-[#C17855] hover:bg-[#B06745] text-white px-10 py-6 text-lg rounded-lg transition-all shadow-md hover:shadow-lg relative" data-testid="guide-cta-btn">
                <span className="absolute -top-3 -right-3 bg-[#6B7A5C] text-white text-xs px-3 py-1 rounded-full font-bold">
                  33% OFF
                </span>
                Get the Full Guide + All Bonuses — $9.99
              </Button>
            </Link>
            <p className="text-[#5A5046] text-sm mt-4">
              Launch special: First 100 buyers - Regular price $14.99
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FreeGuidePage;
